import TechnoArtLogo from "../components/technoArtLogo.tsx";
import React, { useState } from "react";
import Background from "../components/background.tsx";
import Header from "../components/header.tsx";
import Sidebar from "../components/sidebar.tsx";

interface DocTab {
  label: string;
  active?: boolean;
}

const DocumentationPage: React.FC = () => {
  const [activeDocTab, setActiveDocTab] = useState<string>("Overview");
  const [pdfError, setPdfError] = useState<string | null>(null);

  const docTabs: DocTab[] = [
    { label: "Overview", active: activeDocTab === "Overview" },
    { label: "ALPR System", active: activeDocTab === "ALPR System" },
    { label: "AI/ML Services", active: activeDocTab === "AI/ML Services" },
    { label: "REST API", active: activeDocTab === "REST API" },
    { label: "Frontend", active: activeDocTab === "Frontend" },
    { label: "Backend", active: activeDocTab === "Backend" },
    { label: "Database", active: activeDocTab === "Database" },


  ];

  const handleDocTabClick = (tab: string) => {
    setActiveDocTab(tab);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Background />

      <div className="flex relative z-10">
        {/* Sidebar */}
        <Sidebar />

        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <Header />

          {/* Content Area */}
          <div className="p-8">
            <div className="max-w-6xl mx-auto">
              {/* Page Header */}
              <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-t-2xl p-6 shadow-lg">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">
                  Documentation
                </h1>
                <p className="text-gray-600">
                  Learn how to use the Galaxy ALPR system
                </p>
              </div>

              {/* Documentation Tabs */}
              <div className="bg-white bg-opacity-95 backdrop-blur-sm shadow-lg">
                <div className="flex border-b border-gray-200">
                  {docTabs.map((tab, index) => (
                    <button
                      key={index}
                      onClick={() => handleDocTabClick(tab.label)}
                      className={`px-8 py-4 text-sm font-medium transition-colors ${tab.active
                        ? "border-b-2 border-blue-500 text-blue-600 bg-blue-50"
                        : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                        }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Documentation Content */}
              <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-b-2xl p-8 shadow-lg">
                {/* Overview tab content */}
                {activeDocTab === "Overview" && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      What is Galaxy ALPR?
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Automatic License Plate Recognition for Indonesian vehicles
                    </p>

                    <p className="text-gray-700 mb-6 leading-relaxed">
                      Galaxy ALPR is a comprehensive solution for automatic license plate recognition specifically
                      designed for Indonesian vehicles. The system utilizes <span className="font-semibold text-blue-700">YOLOv11n</span> for fast and accurate vehicle and license plate detection, and <span className="font-semibold text-purple-700">Gemini (Gemini 2.0 Flash-Lite)</span> for high-precision optical character recognition (OCR). Together, these technologies enable robust recognition and processing of license plates from various input sources.
                    </p>

                    <p className="text-gray-700 mt-6 leading-relaxed">
                      We have tested Galaxy ALPR on 200 images under various conditions. The system achieved a high accuracy rate of <span className="font-semibold text-green-700">97%</span> with an average processing speed of <span className="font-semibold text-blue-700">2.41 seconds</span> per image.
                    </p>

                    <h3 className="text-lg font-semibold mt-6 text-gray-800 mb-6">
                      Key Features
                    </h3>

                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        Multiple input options: image upload, video upload (upcoming feature), and webcam capture
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        High accuracy license plate detection and recognition
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        Support for different vehicle types (cars, motorcycles)
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        Plate type classification (private, military, public, government, police, diplomatic, free_trade)
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        Comprehensive history tracking and search
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        Statistical dashboard for data analysis
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        RESTful API for integration with other systems
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        Area detection with adjustable regions for improved control
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        Access list management for whitelist and blacklist
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        Location management for defining entry and exit points
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        Upcoming features: user management and automatic resolution of vehicle status when matched at entry and exit locations
                      </li>
                    </ul>
                  </div>
                )}

                {/* ALPR System tab content - replaced with object tag */}
                {activeDocTab === "ALPR System" && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      ALPR System Documentation
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Documentation for the ALPR System codebase
                    </p>

                    <div className="bg-gray-50 p-6 rounded-lg max-w-none" style={{ height: "70vh" }}>
                      <object
                        data="/DOCS_ALPR_SYSTEM.pdf"
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        onError={() => setPdfError("Failed to load PDF document.")}
                      >
                        <div className="flex flex-col items-center justify-center h-full">
                          <p className="text-gray-700">
                            Unable to display PDF.
                            <a
                              href="/DOCS_ALPR_SYSTEM.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline ml-1"
                            >
                              Download the PDF instead
                            </a>
                          </p>
                        </div>
                      </object>

                      {pdfError && (
                        <p className="text-red-500 mt-4 text-center">{pdfError}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* AI/ML Services tab content - replaced with object tag */}
                {activeDocTab === "AI/ML Services" && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      AI/ML Services Documentation
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Documentation for the AI/ML Services codebase
                    </p>

                    <div className="bg-gray-50 p-6 rounded-lg max-w-none" style={{ height: "70vh" }}>
                      <object
                        data="/DOCS_AI_ML_SERVICE.pdf"
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        onError={() => setPdfError("Failed to load PDF document.")}
                      >
                        <div className="flex flex-col items-center justify-center h-full">
                          <p className="text-gray-700">
                            Unable to display PDF.
                            <a
                              href="/DOCS_AI_ML_SERVICE.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline ml-1"
                            >
                              Download the PDF instead
                            </a>
                          </p>
                        </div>
                      </object>

                      {pdfError && (
                        <p className="text-red-500 mt-4 text-center">{pdfError}</p>
                      )}
                    </div>
                  </div>
                )}


                {/* REST API tab content - replaced with object tag */}
                {activeDocTab === "REST API" && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      REST API Documentation
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Documentation for the RESTful API codebase
                    </p>

                    <div className="bg-gray-50 p-6 rounded-lg max-w-none" style={{ height: "70vh" }}>
                      <object
                        data="/DOCS_REST_API.pdf"
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        onError={() => setPdfError("Failed to load PDF document.")}
                      >
                        <div className="flex flex-col items-center justify-center h-full">
                          <p className="text-gray-700">
                            Unable to display PDF.
                            <a
                              href="/DOCS_REST_API.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline ml-1"
                            >
                              Download the PDF instead
                            </a>
                          </p>
                        </div>
                      </object>

                      {pdfError && (
                        <p className="text-red-500 mt-4 text-center">{pdfError}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Frontend tab content - replaced with object tag */}
                {activeDocTab === "Frontend" && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      Frontend Documentation
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Documentation for the Galaxy ALPR frontend codebase
                    </p>

                    <div className="bg-gray-50 p-6 rounded-lg max-w-none" style={{ height: "70vh" }}>
                      <object
                        data="/DOCS_FRONTEND.pdf"
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        onError={() => setPdfError("Failed to load PDF document.")}
                      >
                        <div className="flex flex-col items-center justify-center h-full">
                          <p className="text-gray-700">
                            Unable to display PDF.
                            <a
                              href="/DOCS_FRONTEND.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline ml-1"
                            >
                              Download the PDF instead
                            </a>
                          </p>
                        </div>
                      </object>

                      {pdfError && (
                        <p className="text-red-500 mt-4 text-center">{pdfError}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Backend tab content - replaced with object tag */}
                {activeDocTab === "Backend" && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      Backend Documentation
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Documentation for the Galaxy ALPR backend codebase
                    </p>

                    <div className="bg-gray-50 p-6 rounded-lg max-w-none" style={{ height: "70vh" }}>
                      <object
                        data="/DOCS_BACKEND.pdf"
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        onError={() => setPdfError("Failed to load PDF document.")}
                      >
                        <div className="flex flex-col items-center justify-center h-full">
                          <p className="text-gray-700">
                            Unable to display PDF.
                            <a
                              href="/DOCS_BACKEND.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline ml-1"
                            >
                              Download the PDF instead
                            </a>
                          </p>
                        </div>
                      </object>

                      {pdfError && (
                        <p className="text-red-500 mt-4 text-center">{pdfError}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Database tab content - replaced with object tag */}
                {activeDocTab === "Database" && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      Database Documentation
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Documentation for the Galaxy ALPR database codebase
                    </p>

                    <div className="bg-gray-50 p-6 rounded-lg max-w-none" style={{ height: "70vh" }}>
                      <object
                        data="/DOCS_DATABASE.pdf"
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        onError={() => setPdfError("Failed to load PDF document.")}
                      >
                        <div className="flex flex-col items-center justify-center h-full">
                          <p className="text-gray-700">
                            Unable to display PDF.
                            <a
                              href="/DOCS_DATABASE.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline ml-1"
                            >
                              Download the PDF instead
                            </a>
                          </p>
                        </div>
                      </object>

                      {pdfError && (
                        <p className="text-red-500 mt-4 text-center">{pdfError}</p>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center py-4">
            <p className="text-gray-500 text-opacity-80 text-sm">
              &copy;2025, Galaxy Dev
            </p>
          </div>
        </div>
      </div>
      <TechnoArtLogo />
    </div>
  );
};

export default DocumentationPage;
